import { toNumber } from './dataset.js';
import { sparklineSvg } from './chart-core.js';
const hex = (c) => {
    let h = c.replace('#', '');
    if (h.length === 3)
        h = h.split('').map(x => x + x).join('');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
/** 색 보간: t(0~1) 에서 2색 또는 3색(가운데 0.5) 사이 색 */
export function interpColor(colors, t) {
    t = Math.max(0, Math.min(1, t));
    let a = colors[0], b = colors[colors.length - 1], u = t;
    if (colors.length === 3) {
        if (t <= 0.5) {
            b = colors[1];
            u = t * 2;
        }
        else {
            a = colors[1];
            u = (t - 0.5) * 2;
        }
    }
    const x = hex(a), y = hex(b);
    return `rgb(${x.map((v, i) => Math.round(v + (y[i] - v) * u)).join(',')})`;
}
/** 아이콘: v < thresholds[0] → icons[0], < thresholds[1] → icons[1], 그 외 icons[2] */
export function iconFor(icons, th, v) { return v < th[0] ? icons[0] : v < th[1] ? icons[1] : icons[2]; }
/** 생리적 가능 범위 기본표 (병원별 조정). 컬럼 rules 에 plausibleRule('bt') 처럼 */
export const PLAUSIBLE_RANGES = { bt: [30, 45], hr: [20, 300], sbp: [40, 300], dbp: [20, 200], glu: [10, 1500], age: [0, 130], spo2: [50, 100], rr: [4, 80], wt: [0.3, 400], ht: [20, 250] };
export function plausibleRule(key, ranges = PLAUSIBLE_RANGES) { const [min, max] = ranges[key] ?? [-Infinity, Infinity]; return { kind: 'plausible', min, max }; }
/** 규칙 적용 결과. range(min/max) 는 규칙에 없으면 호출자가 보기 전체에서 구해 넘김 */
export function evalRules(rules, v, row, ds, range) {
    const out = { cls: '', bg: '', prefix: '', style: null };
    const n = toNumber(v);
    for (const r of rules) {
        if (r.kind === 'when') {
            if (r.test(v, row, ds)) {
                if (r.className)
                    out.cls += ' ' + r.className;
                const st = typeof r.style === 'function' ? r.style(v, row, ds) : r.style;
                if (st)
                    out.style = { ...out.style, ...st };
            }
            continue;
        }
        if (n == null || n !== n)
            continue;
        if (r.kind === 'plausible') {
            if (n < r.min || n > r.max)
                out.cls += ' ' + (r.className ?? 'og-implausible');
            continue;
        }
        if (r.kind === 'icon') {
            out.prefix = iconFor(r.icons, r.thresholds, n) + ' ';
            continue;
        }
        const [lo, hi] = range(r), t = hi > lo ? Math.max(0, Math.min(1, (n - lo) / (hi - lo))) : 1;
        if (r.kind === 'dataBar') {
            const p = Math.round(t * 1000) / 10;
            out.bg = `linear-gradient(90deg,${r.color ?? 'rgba(31,111,235,.28)'} ${p}%,transparent ${p}%)`;
        }
        else
            out.bg = interpColor(r.colors, t);
    }
    return out;
}
/** 링크·이미지 주소 허용 목록: http(s)·상대 경로·data:image(래스터·svg) 만. 그 밖(javascript:, vbscript:, data:text/html …)은 빈 값 */
export function safeUrl(u) {
    const s = String(u ?? '').trim();
    if (!s)
        return '';
    // 제어 문자·공백을 끼워 넣은 우회(java\tscript:) 제거 후 스킴 판단
    const t = s.replace(/[\u0000-\u0020\u007f-\u009f]/g, '');
    const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(t);
    if (!m)
        return /^(\/\/)/.test(t) ? '' : s; // 스킴 없는 상대 경로 (//host 는 거부)
    const scheme = m[1].toLowerCase();
    if (scheme === 'http' || scheme === 'https')
        return s;
    if (scheme === 'data' && /^data:image\/(png|gif|jpe?g|webp|bmp|svg\+xml)[;,]/i.test(t))
        return s;
    return '';
}
/** 색 값 허용 목록 (#rgb·#rrggbb·#rrggbbaa·rgb()/rgba()/hsl()·색 이름). 그 밖은 null — 스타일에 다른 선언을 끼워 넣지 못하게 */
export function safeColor(c) {
    const s = String(c ?? '').trim();
    return /^#[0-9a-f]{3,8}$/i.test(s) || /^(rgb|rgba|hsl|hsla)\(\s*[\d.%\s,/-]+\)$/i.test(s) || /^[a-z]{3,20}$/i.test(s) ? s : null;
}
const escH = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/** 스파크라인 값: 배열 또는 "1,2,3" 문자열 */
export function sparkValues(v) {
    const a = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [];
    return a.map(x => { if (x == null || x === '')
        return null; const n = Number(x); return n === n ? n : null; });
}
/** 셀 타입 HTML (일반 셀은 텍스트 노드로 그리고, 이 타입들만 innerHTML) */
export function cellHtml(type, v, text, o = {}) {
    switch (type) {
        case 'progress': {
            const n = Number(v);
            if (v == null || v === '' || n !== n)
                return '';
            const p = Math.max(0, Math.min(100, n));
            return `<span class="og-pg"><i style="width:${p}%"></i><b>${escH(text || Math.round(n) + '%')}</b></span>`;
        }
        case 'badge': return text ? `<span class="og-badge" style="background:${safeColor(o.badgeColors?.[String(v)]) ?? 'var(--og-badge-bg,#e3e8ee)'}">${escH(text)}</span>` : '';
        case 'button': return `<button class="og-cbtn" type="button" tabindex="-1">${escH(text || o.header || '')}</button>`;
        case 'link': return text ? `<a class="og-link" role="link">${escH(text)}</a>` : '';
        case 'image': {
            const src = safeUrl(v);
            return src ? `<img class="og-img" src="${escH(src)}" alt="${escH(text)}">` : '';
        }
        case 'sparkline': return sparklineSvg(sparkValues(v), Math.max(24, (o.width ?? 80) - 12), 18);
    }
    return escH(text);
}
